import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  EXPORT_POLL_MS,
  INVOICE_ID_STORAGE_KEY,
  INVOICES_REPORTS_LEGACY_ROUTES,
  INVOICES_REPORTS_ROUTES,
} from './catalog.js';
import type { Invoice, InvoiceLine, ReportExportRun } from './invoicesReportsApi.js';

export function invoiceListPath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-022'];
}

export function invoiceDetailPath(invoiceId?: string): string {
  if (!invoiceId) return INVOICES_REPORTS_LEGACY_ROUTES['SCR-BIZ-023'];
  return `/invoices/${encodeURIComponent(invoiceId)}`;
}

export function invoiceQueryPath(invoiceId?: string, lineIds?: string[]): string {
  if (!invoiceId) return INVOICES_REPORTS_LEGACY_ROUTES['SCR-BIZ-024'];
  const base = `/invoices/${encodeURIComponent(invoiceId)}/query`;
  const selected = (lineIds ?? []).map((id) => id.trim()).filter(Boolean);
  if (!selected.length) return base;
  return `${base}?lines=${selected.map(encodeURIComponent).join(',')}`;
}

/** Deep-link / detail selection for 024 — `?lines=id,id`. */
export function queryLineIdsFromSearch(search: string): string[] {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const value = new URLSearchParams(raw).get('lines');
  if (!value?.trim()) return [];
  return value
    .split(',')
    .map((id) => decodeURIComponent(id).trim())
    .filter(Boolean);
}

export function paymentRecordPath(invoiceId?: string): string {
  if (!invoiceId) return INVOICES_REPORTS_LEGACY_ROUTES['SCR-BIZ-025'];
  return `/invoices/${encodeURIComponent(invoiceId)}/payment`;
}

export function spendReportPath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-026'];
}

export function exportCentrePath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-027'];
}

export function accountSettingsPath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-028'];
}

export function corporateApiClientsPath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-029'];
}

/** Date input YYYY-MM-DD → create body `expiresAt` (end of that UTC day). */
export function apiClientExpiresAtFromDate(expiresOn?: string): string | undefined {
  const day = expiresOn?.trim();
  if (!day) return undefined;
  return `${day}T23:59:59.000Z`;
}

export function accountActivityPath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-030'];
}

export function alertsPanelPath(): string {
  return INVOICES_REPORTS_ROUTES['SCR-BIZ-031'];
}

export function isPaymentOverlayPath(pathname: string): boolean {
  return /\/invoices\/[^/]+\/payment\/?$/.test(pathname) || pathname === '/payment-record';
}

export function useInvoiceIdParam(): string | null {
  const { id } = useParams();
  const [params] = useSearchParams();
  const fromPath = id?.trim() || null;
  const fromQuery = params.get('invoiceId')?.trim() || null;
  const resolved = fromPath ?? fromQuery ?? getStoredInvoiceId();

  useEffect(() => {
    if (fromPath) setStoredInvoiceId(fromPath);
  }, [fromPath]);

  return resolved;
}

export function getStoredInvoiceId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(INVOICE_ID_STORAGE_KEY);
}

export function setStoredInvoiceId(invoiceId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INVOICE_ID_STORAGE_KEY, invoiceId);
}

export function invoiceLabel(invoice: Invoice): string {
  const num =
    invoice.number ??
    invoice.reference ??
    (invoice.invoiceNumber != null ? String(invoice.invoiceNumber) : null);
  if (num) return `Invoice #${num}`;
  return invoice.period ?? invoice.id ?? 'Invoice';
}

export function poundsToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export function minorToPoundsInput(minor: number | null | undefined): string {
  if (minor == null || !Number.isFinite(minor)) return '';
  return (minor / 100).toFixed(2);
}

export type InvoiceLineGroup = {
  department: string;
  lines: InvoiceLine[];
  netMinor: number;
  taxMinor: number;
  grossMinor: number;
};

export function lineNetMinor(line: InvoiceLine): number {
  return (
    line.netAmountMinor ??
    (line.amountMinor != null && line.vatAmountMinor != null
      ? line.amountMinor - line.vatAmountMinor
      : (line.amountMinor ?? 0))
  );
}

export function lineTaxMinor(line: InvoiceLine): number {
  return line.vatAmountMinor ?? 0;
}

export function lineGrossMinor(line: InvoiceLine): number {
  return line.grossAmountMinor ?? line.amountMinor ?? lineNetMinor(line) + lineTaxMinor(line);
}

export type InvoiceLineGroupBy = 'department' | 'costCentre';

export function lineGroupKey(line: InvoiceLine, groupBy: InvoiceLineGroupBy): string {
  if (groupBy === 'costCentre') return line.costCentreCode?.trim() || 'Unassigned';
  return line.departmentName?.trim() || 'Unassigned';
}

export function groupInvoiceLines(
  lines: InvoiceLine[],
  groupBy: InvoiceLineGroupBy = 'department',
): InvoiceLineGroup[] {
  const order: string[] = [];
  const map = new Map<string, InvoiceLine[]>();
  for (const line of lines) {
    const key = lineGroupKey(line, groupBy);
    if (!map.has(key)) {
      order.push(key);
      map.set(key, []);
    }
    map.get(key)!.push(line);
  }
  return order.map((department) => {
    const grouped = map.get(department) ?? [];
    return {
      department,
      lines: grouped,
      netMinor: grouped.reduce((sum, line) => sum + lineNetMinor(line), 0),
      taxMinor: grouped.reduce((sum, line) => sum + lineTaxMinor(line), 0),
      grossMinor: grouped.reduce((sum, line) => sum + lineGrossMinor(line), 0),
    };
  });
}

export function groupInvoiceLinesByDepartment(lines: InvoiceLine[]): InvoiceLineGroup[] {
  return groupInvoiceLines(lines, 'department');
}

export function lineIsCredit(line: InvoiceLine): boolean {
  return (line.lineType ?? '').toUpperCase() === 'CREDIT';
}

export function creditReferencesOriginal(line: InvoiceLine): string | null {
  const ref = line.originalLineId ?? line.creditOfLineId ?? line.orderReference ?? line.orderId;
  return ref?.trim() || null;
}

export function querySelectionAnnouncement(
  count: number,
  selectedMinor: number,
  currency: string,
  formatMoney: (minor: number, currency?: string) => string,
): string {
  if (count === 0) return 'No lines selected for a query.';
  const lines = count === 1 ? '1 line selected' : `${count} lines selected`;
  return `${lines}, ${formatMoney(selectedMinor, currency)}`;
}

export function taxBreakdownFromLines(
  lines: InvoiceLine[],
  invoiceVatMinor?: number | null,
): { label: string; minor: number }[] {
  const byRate = new Map<string, number>();
  for (const line of lines) {
    const rate = line.vatRate ?? line.taxRate;
    const vat = lineTaxMinor(line);
    if (rate != null && rate !== '') {
      const key = `${String(rate).replace(/%$/, '')}%`;
      byRate.set(key, (byRate.get(key) ?? 0) + vat);
    }
  }
  if (byRate.size > 0) {
    return [...byRate.entries()].map(([label, minor]) => ({ label, minor }));
  }
  if (invoiceVatMinor != null) return [{ label: 'VAT', minor: invoiceVatMinor }];
  return [];
}

const EXPORT_TERMINAL = new Set([
  'READY',
  'AVAILABLE',
  'FAILED',
  'EXPIRED',
  'CANCELLED',
  'COMPLETE',
]);

export function exportRunIsTerminal(state?: string | null): boolean {
  return EXPORT_TERMINAL.has((state ?? '').toUpperCase());
}

export function exportRunIsReady(state?: string | null): boolean {
  const s = (state ?? '').toUpperCase();
  return s === 'READY' || s === 'AVAILABLE' || s === 'COMPLETE';
}

export function exportsNeedPolling(runs: ReportExportRun[]): boolean {
  return runs.some((run) => !exportRunIsTerminal(run.state));
}

/** 027 ○ — poll while a run is in flight; stop when every job is terminal. */
export function exportPollInterval(runs: ReportExportRun[]): number | false {
  return exportsNeedPolling(runs) ? EXPORT_POLL_MS : false;
}

export function canSubmitInvoiceQuery(reason: string, lineIds: string[]): boolean {
  return Boolean(reason.trim()) && lineIds.length > 0;
}

export const QUERY_EVIDENCE_MAX_FILES = 5;
export const QUERY_EVIDENCE_MAX_BYTES = 2 * 1024 * 1024;
export const QUERY_EVIDENCE_ACCEPT =
  'application/pdf,image/png,image/jpeg,image/webp,text/plain,.pdf,.png,.jpg,.jpeg,.webp,.txt';

export type QueryEvidenceUpload = {
  filename: string;
  contentType?: string;
  contentBase64?: string;
};

export function assertEvidenceFiles(files: File[]): string | null {
  if (files.length > QUERY_EVIDENCE_MAX_FILES) {
    return `Attach at most ${QUERY_EVIDENCE_MAX_FILES} files.`;
  }
  const tooBig = files.find((file) => file.size > QUERY_EVIDENCE_MAX_BYTES);
  if (tooBig) return `${tooBig.name} is over 2 MB.`;
  return null;
}

export function readEvidenceFile(file: File): Promise<QueryEvidenceUpload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      const contentBase64 = comma >= 0 ? result.slice(comma + 1) : result;
      resolve({
        filename: file.name,
        contentType: file.type || undefined,
        contentBase64,
      });
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

export function spendGroupColumnLabel(groupBy: string): string {
  switch (groupBy) {
    case 'department':
      return 'Department';
    case 'costcentre':
      return 'Cost centre';
    case 'employee':
      return 'Employee';
    case 'vertical':
      return 'Vertical';
    case 'none':
      return 'Order';
    default:
      return 'Group';
  }
}

export function spendGroupByOptions(employeeLevelAllowed: boolean): {
  value: string;
  label: string;
}[] {
  const options = [
    { value: 'department', label: 'Department' },
    { value: 'costcentre', label: 'Cost centre' },
  ];
  if (employeeLevelAllowed) {
    options.push({ value: 'employee', label: 'Employee' });
  }
  options.push({ value: 'vertical', label: 'Vertical' }, { value: 'none', label: 'Order lines' });
  return options;
}

export function activityToCsv(
  events: Array<{
    summary?: string | null;
    action?: string | null;
    category?: string | null;
    createdAt?: string | null;
  }>,
): string {
  const header = 'time,category,summary';
  const rows = events.map((event) => {
    const cells = [
      event.createdAt ?? '',
      event.category ?? '',
      event.summary ?? event.action ?? '',
    ].map((cell) => `"${String(cell).replace(/"/g, '""')}"`);
    return cells.join(',');
  });
  return [header, ...rows].join('\n');
}

export function downloadTextFile(filename: string, contents: string, mime = 'text/csv'): void {
  const blob = new Blob([contents], { type: mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}
